import { useEffect, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { Bell, CheckCheck, CheckCircle2, Megaphone, ReceiptText, Sparkles } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { ScrollArea } from '@/components/ui/scroll-area'
import { appFetch } from '@/shared/api/app-client'
import type { ApiResponse, NotificationInboxData, NotificationCategory, UserNotification } from '@/shared/api/app-types'
import { cn } from '@/shared/ui/cn'

const categoryIcon: Record<NotificationCategory, typeof Bell> = {
  billing: ReceiptText,
  task: CheckCircle2,
  system: Sparkles,
  announcement: Megaphone,
}

function formatNotificationTime(value: string) {
  const elapsed = Date.now() - new Date(value).getTime()
  const minutes = Math.max(0, Math.floor(elapsed / 60_000))
  if (minutes < 1) return '刚刚'
  if (minutes < 60) return `${minutes} 分钟前`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} 小时前`
  const days = Math.floor(hours / 24)
  return days < 7 ? `${days} 天前` : new Date(value).toLocaleDateString('zh-CN')
}

export function NotificationInbox() {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<NotificationInboxData>({ unreadCount: 0, items: [] })

  const load = async () => {
    setLoading(true)
    try {
      const response = await appFetch<ApiResponse<NotificationInboxData>>('/api/notifications')
      setData(response.data)
    } catch {
      // Notification delivery must not interrupt editing if the API is temporarily unavailable.
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void load() }, [])
  useEffect(() => {
    if (!open) return
    void load()
  }, [open])

  const markRead = async (notification: UserNotification) => {
    if (notification.readAt) return
    setData((current) => ({
      unreadCount: Math.max(0, current.unreadCount - 1),
      items: current.items.map((item) => item.id === notification.id ? { ...item, readAt: new Date().toISOString() } : item),
    }))
    try {
      const response = await appFetch<ApiResponse<{ unreadCount: number }>>(`/api/notifications/${notification.id}/read`, { method: 'PATCH' })
      setData((current) => ({ ...current, unreadCount: response.data.unreadCount }))
    } catch {
      void load()
    }
  }

  const markAllRead = async () => {
    if (!data.unreadCount) return
    setData((current) => ({ unreadCount: 0, items: current.items.map((item) => ({ ...item, readAt: item.readAt ?? new Date().toISOString() })) }))
    try {
      await appFetch<ApiResponse<{ unreadCount: number }>>('/api/notifications/read-all', { method: 'POST' })
    } catch {
      void load()
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="relative -m-2 p-2 text-muted-foreground transition-colors hover:text-foreground" aria-label={data.unreadCount ? `通知，${data.unreadCount} 条未读` : '通知'}>
          <Bell className="size-5" />
          {data.unreadCount ? <span className="absolute right-1 top-1 grid min-w-4 place-items-center rounded-full bg-primary px-1 text-[9px] font-bold leading-4 text-primary-foreground">{data.unreadCount > 99 ? '99+' : data.unreadCount}</span> : null}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={14} className="w-[min(380px,calc(100vw-24px))] overflow-hidden border-border bg-popover p-0 text-popover-foreground shadow-2xl">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <div><p className="text-sm font-semibold">通知</p><p className="mt-0.5 text-xs text-muted-foreground">账户、任务与产品动态</p></div>
          <button type="button" onClick={() => void markAllRead()} disabled={!data.unreadCount} className="inline-flex items-center gap-1 text-xs font-medium text-primary transition-colors hover:text-primary/80 disabled:cursor-not-allowed disabled:opacity-40"><CheckCheck className="size-3.5" />全部已读</button>
        </div>
        <ScrollArea className="max-h-[420px]">
          {loading && !data.items.length ? <div className="px-4 py-10 text-center text-sm text-muted-foreground">正在加载通知…</div> : null}
          {!loading && !data.items.length ? <div className="px-4 py-12 text-center"><Bell className="mx-auto size-5 text-muted-foreground" /><p className="mt-3 text-sm font-medium">暂无通知</p><p className="mt-1 text-xs text-muted-foreground">支付、任务和产品动态会显示在这里。</p></div> : null}
          <div className="divide-y divide-border">
            {data.items.map((notification) => <NotificationRow key={notification.id} notification={notification} onRead={markRead} onNavigate={() => setOpen(false)} />)}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  )
}

function NotificationRow({ notification, onRead, onNavigate }: { notification: UserNotification; onRead: (item: UserNotification) => Promise<void>; onNavigate: () => void }) {
  const Icon = categoryIcon[notification.category]
  const isAnnouncement = notification.category === 'announcement' && notification.announcementId
  const content = <><div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary"><Icon className="size-4" /></div><div className="min-w-0 flex-1"><div className="flex items-start gap-2"><p className="min-w-0 flex-1 text-sm font-medium leading-5 text-foreground">{notification.title}</p>{!notification.readAt ? <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-primary" aria-label="未读" /> : null}</div><p className="mt-1 line-clamp-3 text-xs leading-5 text-muted-foreground">{notification.body}</p><div className="mt-2 flex items-center justify-between gap-3"><span className="text-[11px] text-muted-foreground">{formatNotificationTime(notification.createdAt)}</span>{isAnnouncement || notification.actionLabel ? <span className="text-[11px] font-semibold text-primary">{isAnnouncement ? '查看' : notification.actionLabel} →</span> : null}</div></div></>
  const className = cn('flex w-full gap-3 px-4 py-3.5 text-left transition-colors hover:bg-accent/70', !notification.readAt && 'bg-primary/[.045]')
  if (isAnnouncement) return <Link to="/announcements/$announcementId" params={{ announcementId: String(notification.announcementId) }} className={className} onClick={() => { void onRead(notification); onNavigate() }}>{content}</Link>
  return notification.actionUrl ? <a href={notification.actionUrl} className={className} onClick={() => void onRead(notification)}>{content}</a> : <button type="button" className={className} onClick={() => void onRead(notification)}>{content}</button>
}
